import type { ReactNode } from 'react';

import {
  BpkSpinner,
  SPINNER_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-spinner';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classnames from 'classnames';

import {
  FormattedMessage,
  withI18n,
} from '../../../hotels-utils/services/i18n';
import { getId, getAriaHidden } from '../../../utils/a11y';
import RoomRateAmenities from '../RoomRateAmenities';

import type {
  I18n,
  Price,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/types';

import STYLES from './MainPriceMobile.module.scss';

type MainPriceMobileProps = {
  i18n: I18n;
  mainPrice: Price;
  priceBadge?: ReactNode;
  price?: number;
  isDefaultPerNight: boolean;
  originalNights: number;
  distance?: ReactNode;
  originalPrice?: string;
  loading?: boolean;
  hotelCardAccessibility?: boolean;
  index?: number;
  logoNode?: ReactNode;
  showDivider?: boolean;
};

const MainPriceMobile = ({
  distance = undefined,
  hotelCardAccessibility = false,
  i18n,
  index = undefined,
  isDefaultPerNight,
  loading = false,
  logoNode = undefined,
  mainPrice,
  originalNights,
  originalPrice = undefined,
  price = undefined,
  priceBadge = undefined,
  showDivider = false,
}: MainPriceMobileProps) => {
  const { additionalPrice, amenities } = mainPrice;
  const originalPriceAriaOneNight = i18n.translate(
    'aria_originPriceOneNight',
    {
      price: originalPrice,
    },
    '{',
    '}',
    true,
  );
  const originalPriceAriaTotal = i18n.translate(
    'aria_originPriceTotal',
    {
      price: originalPrice,
    },
    '{',
    '}',
    true,
  );
  const buildPriceInfo = () => {
    const additionalPriceWithC = additionalPrice
      ? i18n.formatCurrency(additionalPrice)
      : undefined;
    let additionalPriceText = null;
    if (originalNights === 1) {
      const mainKey = 'dayView_hotel_card_a_night';
      const mainPriceText = <FormattedMessage label={mainKey} />;
      return {
        mainPriceText,
        additionalPriceText,
        originalPriceAria: originalPriceAriaOneNight,
      };
    }

    if (isDefaultPerNight) {
      additionalPriceText = (
        <FormattedMessage
          label="OTARate_label_priceForNights"
          cardinality={originalNights}
          data={{ price: additionalPriceWithC }}
        />
      );
      return {
        mainPriceText: <FormattedMessage label="dayView_hotel_card_a_night" />,
        additionalPriceText,
        originalPriceAria: originalPriceAriaOneNight,
      };
    }
    additionalPriceText = (
      <FormattedMessage
        label="OTARate_label_priceForANight"
        data={{ price: additionalPriceWithC }}
      />
    );
    return {
      mainPriceText: <FormattedMessage label="dayView_hotel_card_total" />,
      additionalPriceText,
      originalPriceAria: originalPriceAriaTotal,
    };
  };

  const priceInfoDesc = buildPriceInfo();
  const cleanAndPrice = (
    <div
      className={classnames(STYLES.cleanAndPrice, {
        [STYLES.accessibility]: hotelCardAccessibility,
      })}
    >
      {i18n.translate('hotel_card_price_from')}

      {!priceBadge && logoNode}
      <div
        className={classnames(STYLES.subMainPriceInfo, {
          [STYLES.accessibility]: hotelCardAccessibility,
        })}
      >
        {loading && (
          <BpkSpinner
            type={SPINNER_TYPES.primary}
            className={STYLES.priceNodeSpinner}
          />
        )}
        {originalPrice && (
          <>
            {hotelCardAccessibility ? (
              <BpkText
                color={TEXT_COLORS.textSecondary}
                textStyle={TEXT_STYLES.caption}
                id={getId('hotel_original_price', index)}
                aria-label={priceInfoDesc.originalPriceAria}
                aria-hidden={getAriaHidden(index)}
                className={classnames(STYLES.originalPrice)}
              >
                {originalPrice}
              </BpkText>
            ) : (
              <BpkText
                color={TEXT_COLORS.textSecondary}
                textStyle={TEXT_STYLES.footnote}
                id={getId('hotel_original_price', index)}
                aria-label={priceInfoDesc.originalPriceAria}
                aria-hidden={getAriaHidden(index)}
                className={classnames(STYLES.originalPrice)}
              >
                {originalPrice}
              </BpkText>
            )}
          </>
        )}
        <BpkText
          textStyle={TEXT_STYLES.heading4}
          className={classnames(STYLES.mainPrice, {
            [STYLES.accessibility]: hotelCardAccessibility,
          })}
          id={getId('hotel_price', index)}
          aria-hidden={getAriaHidden(index)}
        >
          {price && i18n.formatCurrency(price)}
        </BpkText>

        {hotelCardAccessibility ? (
          <BpkText
            textStyle={TEXT_STYLES.caption}
            className={STYLES.mainPriceText}
            id={getId('hotel_nights', index)}
            aria-hidden={getAriaHidden(index)}
          >
            {priceInfoDesc.mainPriceText}
          </BpkText>
        ) : (
          <BpkText
            textStyle={TEXT_STYLES.footnote}
            className={STYLES.mainPriceText}
            id={getId('hotel_nights', index)}
            aria-hidden={getAriaHidden(index)}
          >
            {priceInfoDesc.mainPriceText}
          </BpkText>
        )}
      </div>
    </div>
  );

  if (hotelCardAccessibility) {
    let badgeRow;
    if (priceBadge && logoNode) {
      badgeRow = (
        <div className={STYLES.badgeWithLogo}>
          {logoNode}
          {priceBadge}
        </div>
      );
    } else if (priceBadge) {
      badgeRow = priceBadge;
    }
    return (
      <div className={STYLES.MainPriceMobile}>
        {showDivider && <div className={STYLES.divider} />}
        {badgeRow}
        {cleanAndPrice}
        <RoomRateAmenities
          amenities={amenities}
          hotelCardAccessibility={hotelCardAccessibility}
          index={index}
        />
      </div>
    );
  }

  return (
    <div className={STYLES.MainPriceMobile}>
      <div className={STYLES.distanceAndBadge}>
        <div className={STYLES.distance}>{distance}</div>
        {priceBadge || cleanAndPrice}
      </div>
      {priceBadge ? cleanAndPrice : null}
      <RoomRateAmenities
        amenities={amenities}
        className={classnames(STYLES.amenity, STYLES.amenityItem)}
      />
    </div>
  );
};

export default withI18n(MainPriceMobile);
